import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Menu, 
  X, 
  User, 
  Settings, 
  LogOut,
  Home,
  Users,
  CreditCard,
  BarChart3,
  MessageSquare,
  UserCog,
  Shield,
  Palette,
  Globe,
  DollarSign,
  Trophy,
  MapPin
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-basketball-gradient rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">🏀</span>
            </div>
            <div className="flex flex-col">
              <span className="font-orbitron font-bold text-basketball-blue-500">OG Basketball</span>
              <span className="text-xs text-gray-500">Tanzania Club</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-700 hover:text-basketball-orange-500 transition-colors">
              Home
            </Link>
            
            {isAuthenticated && (
              <>
                {isAdmin ? (
                  <>
                    <Link to="/admin" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1">
                      <BarChart3 className="w-4 h-4" />
                      <span>Dashboard</span>
                    </Link>
                    <Link to="/admin/members" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>Members</span>
                    </Link>
                    <Link to="/admin/digital-cards" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1">
                      <CreditCard className="w-4 h-4" />
                      <span>Digital Cards</span>
                    </Link>
                    <Link to="/admin/sms" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1">
                      <MessageSquare className="w-4 h-4" />
                      <span>SMS</span>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1 px-2 focus-visible:ring-0">
                          <Settings className="w-4 h-4" />
                          <span>Configuration</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>Admin Configuration</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin/settings" className="flex items-center">
                            <Shield className="w-4 h-4 mr-2" />
                            System Settings
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/member-types" className="flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Member Types
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/playing-levels" className="flex items-center">
                            <Trophy className="w-4 h-4 mr-2" />
                            Playing Levels
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/playing-positions" className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2" />
                            Playing Positions
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <Link to="/dashboard" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1">
                      <Home className="w-4 h-4" />
                      <span>Dashboard</span>
                    </Link>
                    <Link to="/my-card" className="text-gray-700 hover:text-basketball-orange-500 transition-colors flex items-center space-x-1">
                      <CreditCard className="w-4 h-4" />
                      <span>My Card</span>
                    </Link>
                  </>
                )}
              </>
            )}

            {!isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button variant="outline" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-basketball-gradient hover:opacity-90">
                    Join Club
                  </Button>
                </Link>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-basketball-gradient rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span>{user?.first_name || user?.name || user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {!isAdmin && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/payments" className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-2" />
                          Payments
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/preferences" className="flex items-center">
                          <Palette className="w-4 h-4 mr-2" />
                          Preferences
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/settings" className="flex items-center">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Settings
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button variant="ghost" size="sm" onClick={toggleMobileMenu}>
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              
              {isAuthenticated && (
                <>
                  {isAdmin ? (
                    <>
                      <Link
                        to="/admin"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                      <Link
                        to="/admin/members"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Members
                      </Link>
                      <Link
                        to="/admin/digital-cards"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Digital Cards
                      </Link>
                      <Link
                        to="/admin/sms"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        SMS Management
                      </Link>
                      <Link
                        to="/admin/settings"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Admin Settings
                      </Link>
                      <Link
                        to="/admin/member-types"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Member Types
                      </Link>
                      <Link
                        to="/admin/playing-levels"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Playing Levels
                      </Link>
                      <Link
                        to="/admin/playing-positions"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Playing Positions
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/dashboard"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/my-card"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        My Card
                      </Link>
                      <Link
                        to="/payments"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Payments
                      </Link>
                      <Link
                        to="/preferences"
                        className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Preferences
                      </Link>
                    </>
                  )}
                  <Link
                    to="/profile"
                    className="block px-3 py-2 text-gray-700 hover:text-basketball-orange-500"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              )}

              {!isAuthenticated && (
                <div className="space-y-2 px-3 py-2">
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="w-full bg-basketball-gradient hover:opacity-90">
                      Join Club
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
